<!-- Raw ADT message log. See kaironlibrary app_modules/worklist/patient_drawer. -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-gray-900">ADT Events</h3>
        <p class="text-[10px] text-gray-400 mt-0.5">
          {{ events.length }} HL7 messages as received — no grouping, no dedup
        </p>
      </div>
      <div class="flex items-center gap-3">
        <select
          v-model.number="months"
          class="text-[11px] border border-gray-300 rounded px-2 py-1 bg-white"
          @change="fetchEvents"
        >
          <option :value="3">3 months</option>
          <option :value="12">12 months</option>
          <option :value="24">24 months</option>
          <option :value="60">All</option>
        </select>
        <button
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          @click="fetchEvents"
        >
          <FontAwesomeIcon :icon="['fas', 'sync']" :class="{ 'animate-spin': loading }" class="text-sm" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="p-4 space-y-1.5">
      <div v-for="n in 6" :key="'sk-' + n" class="h-8 bg-gray-100 rounded animate-pulse"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!events.length" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'exchange-alt']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No ADT messages found</p>
    </div>

    <!-- Content -->
    <div v-else class="p-4">
      <!-- Facet counts are over the whole result, not the filtered view. -->
      <div class="flex flex-wrap items-center gap-1.5 mb-2 pb-2 border-b border-gray-200">
        <span class="text-[10px] text-gray-400 uppercase tracking-wider mr-0.5">Trigger</span>
        <button
          class="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold"
          :class="!selectedTrigger ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="selectedTrigger = null"
        >all {{ events.length }}</button>
        <button
          v-for="(n, t) in triggerCounts"
          :key="t"
          class="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold"
          :class="selectedTrigger === t ? 'bg-gray-800 text-white' : triggerClass(t) + ' hover:opacity-80'"
          :title="triggerLabel(t)"
          @click="selectedTrigger = selectedTrigger === t ? null : t"
        >{{ t }} · {{ triggerLabel(t) }} {{ n }}</button>
      </div>

      <div v-if="Object.keys(senderCounts).length > 1"
           class="flex flex-wrap items-center gap-1.5 mb-3 pb-2 border-b border-gray-200">
        <span class="text-[10px] text-gray-400 uppercase tracking-wider mr-0.5">Sender</span>
        <button
          class="px-1.5 py-0.5 rounded text-[10px] font-medium"
          :class="!selectedSender ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="selectedSender = null"
        >all</button>
        <button
          v-for="(n, sender) in senderCounts"
          :key="sender"
          class="px-1.5 py-0.5 rounded text-[10px] font-medium"
          :class="selectedSender === sender ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="selectedSender = selectedSender === sender ? null : sender"
        >{{ sender }} {{ n }}</button>
      </div>

      <p v-if="truncated" class="text-[10px] text-amber-600 mb-2">
        Showing the {{ events.length }} most recent messages — older ones are not displayed.
      </p>

      <!-- Stream. One row per HL7 message, newest first. NO date headers: the
           moment you bucket by day you are grouping, which is the thing this
           view exists NOT to do. The date is on every row instead. -->
      <div class="space-y-px">
        <div v-for="e in visibleEvents" :key="e.MESSAGE_ID">
          <div
            class="flex items-start gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer border-b border-gray-50"
            @click="expanded === e.MESSAGE_ID ? expanded = null : expanded = e.MESSAGE_ID"
          >
            <!-- Received -->
            <div class="w-[104px] shrink-0 text-[11px] font-mono text-gray-500 pt-0.5">
              {{ stamp(e.MESSAGE_DATETIME) }}
            </div>

            <!-- Trigger -->
            <div class="w-9 shrink-0 pt-0.5">
              <span class="text-[10px] font-mono font-semibold px-1 py-px rounded"
                    :class="triggerClass(e.TRIGGER_EVENT)"
                    :title="e.TRIGGER_EVENT + ' — ' + triggerLabel(e.TRIGGER_EVENT)">{{ e.TRIGGER_EVENT || '??' }}</span>
            </div>

            <!-- Class -->
            <div class="w-7 shrink-0 pt-0.5 text-center">
              <span class="text-[10px] font-mono font-semibold" :class="classColor(e.PATIENT_CLASS_CODE)"
                    :title="classLabel(e.PATIENT_CLASS_CODE)">{{ e.PATIENT_CLASS_CODE || '·' }}</span>
            </div>

            <!-- Body: the common vars, on the row -->
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5 flex-wrap text-[11px]">
                <span class="text-gray-900 font-medium">{{ eventPhrase(e) }}</span>
                <span class="text-gray-500 truncate max-w-[240px]">{{ e.LOCATION_FACILITY || '—' }}</span>
                <span v-if="e.LOCATION_UNIT" class="text-gray-500">/ {{ e.LOCATION_UNIT }}</span>
                <span v-if="e.VISIT_NUMBER" class="font-mono text-gray-400">#{{ e.VISIT_NUMBER }}</span>
              </div>
              <div class="flex items-baseline gap-2 flex-wrap text-[10px] text-gray-400 mt-px">
                <span>via {{ e.SENDING_APPLICATION || '—' }}</span>
                <span v-if="e.ADMIT_DATETIME">adm {{ shortStamp(e.ADMIT_DATETIME) }}</span>
                <span v-if="e.DISCHARGE_DATETIME">dc {{ shortStamp(e.DISCHARGE_DATETIME) }}</span>
                <span v-if="e.VISIT_DISCHARGE_DISPOSITION" class="text-gray-500">
                  &rarr; {{ e.VISIT_DISCHARGE_DISPOSITION }}
                </span>
                <span v-if="e.ATTENDING_DOCTOR">attn {{ e.ATTENDING_DOCTOR }}</span>
                <span v-if="e.HOSPITAL_SERVICE">svc {{ e.HOSPITAL_SERVICE }}</span>
                <span v-if="e.DIAGNOSES" class="text-blue-500 truncate max-w-[300px]">dx {{ e.DIAGNOSES }}</span>
              </div>
            </div>

            <FontAwesomeIcon
              :icon="['fas', expanded === e.MESSAGE_ID ? 'chevron-up' : 'chevron-down']"
              class="text-[9px] text-gray-300 mt-1 shrink-0"
            />
          </div>

          <div v-if="expanded === e.MESSAGE_ID"
               class="mb-2 px-3 py-2 bg-gray-50 rounded border border-gray-100">
            <div class="grid grid-cols-2 gap-x-4 gap-y-1">
              <div v-for="f in detailFields(e)" :key="f.label" class="text-[11px]">
                <span class="text-gray-400">{{ f.label }}:</span>
                <span class="ml-1 text-gray-700 font-mono break-all">{{ f.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// HL7 v2 trigger events (MSH-9.2). No crosswalk exists in Snowflake.
const TRIGGER_LABEL = {
  A01: 'Admit',
  A02: 'Transfer',
  A03: 'Discharge',
  A04: 'Register',
  A05: 'Pre-admit',
  A06: 'Outpatient to inpatient',
  A07: 'Inpatient to outpatient',
  A08: 'Update patient info',
  A09: 'Depart tracking',
  A10: 'Arrive tracking',
  A11: 'Cancel admit',
  A12: 'Cancel transfer',
  A13: 'Cancel discharge',
  A14: 'Pending admit',
  A15: 'Pending transfer',
  A16: 'Pending discharge',
  A17: 'Swap patients',
  A18: 'Merge patient info',
  A20: 'Bed status update',
  A21: 'Leave of absence — out',
  A22: 'Leave of absence — return',
  A23: 'Delete a patient record',
  A25: 'Cancel pending discharge',
  A26: 'Cancel pending transfer',
  A27: 'Cancel pending admit',
  A28: 'Add person information',
  A29: 'Delete person information',
  A30: 'Merge person information',
  A31: 'Update person information',
  A35: 'Merge account numbers',
  A38: 'Cancel pre-admit',
  A40: 'Merge patient — identifier list',
  A42: 'Merge visit',
  A44: 'Move account information',
  A45: 'Move visit information',
  A47: 'Change patient identifier list',
  A52: 'Cancel leave of absence — out',
  A53: 'Cancel leave of absence — return',
  A60: 'Update adverse reaction',
};

const TRIGGER_STYLE = {
  A01: 'bg-red-100 text-red-700',
  A04: 'bg-orange-100 text-orange-700',
  A10: 'bg-orange-100 text-orange-700',
  A03: 'bg-green-100 text-green-700',
  A02: 'bg-amber-100 text-amber-700',
  A06: 'bg-amber-100 text-amber-700',
  A07: 'bg-amber-100 text-amber-700',
  A05: 'bg-gray-100 text-gray-600',
  A14: 'bg-gray-100 text-gray-600',
  A15: 'bg-gray-100 text-gray-600',
  A16: 'bg-gray-100 text-gray-600',
  A08: 'bg-blue-100 text-blue-700',
  A28: 'bg-blue-100 text-blue-700',
  A31: 'bg-blue-100 text-blue-700',
};
// Corrections, not clinical movement.
const TRIGGER_MUTED = new Set([
  'A11', 'A12', 'A13', 'A25', 'A26', 'A27', 'A38', 'A52', 'A53',
  'A17', 'A18', 'A23', 'A29', 'A30', 'A35', 'A40', 'A42', 'A44', 'A45', 'A47',
]);

// HL7 table 0004 patient class (PV1-2).
const CLASS_LABEL = {
  I: 'Inpatient', O: 'Outpatient', E: 'Emergency', P: 'Preadmit',
  R: 'Recurring', B: 'Obstetrics', C: 'Commercial account',
  N: 'Not applicable', U: 'Unknown', V: 'Observation',
};
const CLASS_COLOR = {
  I: 'text-red-600', E: 'text-orange-600', V: 'text-purple-600',
  B: 'text-purple-600', O: 'text-blue-500', P: 'text-gray-400',
  R: 'text-teal-600',
};

// Mirrors how ADT_CLEAN_EVENTS derives EVENT_TYPE (trigger + class + patient type).
const CLASS_PREFIX = { E: 'ED', I: 'Inpatient', O: 'Outpatient', V: 'Observation', P: 'Pre-admit' };

export default {
  name: 'ADT_Event_Stream',
  components: { FontAwesomeIcon },
  props: {
    patientIdentifier: { type: String, required: true },
  },
  data() {
    return {
      events: [],
      loading: false,
      truncated: false,
      months: 24,
      selectedTrigger: null,
      selectedSender: null,
      expanded: null,
    };
  },
  computed: {
    triggerCounts() {
      return this.tally(this.events.map(e => e.TRIGGER_EVENT || '??'));
    },
    senderCounts() {
      return this.tally(this.events.map(e => e.SENDING_APPLICATION || 'unknown'));
    },
    visibleEvents() {
      return this.events.filter(e => {
        if (this.selectedTrigger && (e.TRIGGER_EVENT || '??') !== this.selectedTrigger) return false;
        if (this.selectedSender && (e.SENDING_APPLICATION || 'unknown') !== this.selectedSender) return false;
        return true;
      });
    },
  },
  watch: {
    patientIdentifier: 'fetchEvents',
  },
  mounted() {
    this.fetchEvents();
  },
  methods: {
    tally(values) {
      const c = {};
      for (const v of values) c[v] = (c[v] || 0) + 1;
      return Object.fromEntries(Object.entries(c).sort((a, b) => b[1] - a[1]));
    },

    async fetchEvents() {
      if (!this.patientIdentifier) return;
      this.loading = true;
      try {
        const { data } = await this.$axios.get(
          `/api/patient/${this.patientIdentifier}/adt-events/`,
          { params: { months: this.months } },
        );
        this.events = data.events || [];
        this.truncated = !!data.truncated;
      } catch (err) {
        console.error('Failed to load ADT messages:', err);
        this.events = [];
      } finally {
        this.loading = false;
      }
    },

    triggerClass(t) {
      if (TRIGGER_STYLE[t]) return TRIGGER_STYLE[t];
      if (TRIGGER_MUTED.has(t)) return 'bg-gray-100 text-gray-400 line-through';
      return 'bg-gray-100 text-gray-500';
    },
    triggerLabel(t) {
      return TRIGGER_LABEL[t] || 'Unmapped trigger';
    },
    eventPhrase(e) {
      const base = TRIGGER_LABEL[e.TRIGGER_EVENT];
      if (!base) return `Unmapped (${e.TRIGGER_EVENT || '??'})`;
      const obs = String(e.PATIENT_TYPE || '').toUpperCase().includes('OBS');
      const prefix = obs ? 'Observation' : CLASS_PREFIX[e.PATIENT_CLASS_CODE];
      return prefix ? `${prefix} ${base.toLowerCase()}` : base;
    },
    classLabel(c) {
      return CLASS_LABEL[c] || c || 'not stated';
    },
    classColor(c) {
      return CLASS_COLOR[c] || 'text-gray-400';
    },

    // String slicing, not Date parsing: these are wall-clock times with no zone.
    stamp(iso) {
      if (!iso) return '';
      const s = String(iso);
      const [y, m, d] = s.slice(0, 10).split('-');
      const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const mon = MON[Number(m) - 1] || m;
      return `${mon} ${Number(d)} ${s.slice(11, 16)}`.trim() + (y ? '' : '');
    },
    shortStamp(iso) {
      if (!iso) return '';
      const s = String(iso);
      return `${s.slice(5, 10)} ${s.slice(11, 16)}`;
    },

    detailFields(e) {
      const rows = [
        ['Received', e.MESSAGE_DATETIME],
        ['Recorded (EVN)', e.RECORDED_DATETIME],
        ['Message type', [e.MESSAGE_CODE, e.TRIGGER_EVENT].filter(Boolean).join('^')],
        ['Trigger meaning', e.TRIGGER_EVENT ? this.triggerLabel(e.TRIGGER_EVENT) : null],
        ['Event code', e.EVENT_TYPE_CODE],
        ['Event reason', e.EVENT_REASON_CODE],
        ['Sending app', e.SENDING_APPLICATION],
        ['Sending facility', e.SENDING_FACILITY],
        ['Patient class', e.PATIENT_CLASS_CODE ? `${e.PATIENT_CLASS_CODE} (${this.classLabel(e.PATIENT_CLASS_CODE)})` : null],
        ['Patient type', e.PATIENT_TYPE],
        ['Facility', e.LOCATION_FACILITY],
        ['Unit', e.LOCATION_UNIT],
        ['Prior location', e.PRIOR_LOCATION],
        ['Visit number', e.VISIT_NUMBER],
        ['Admission type', e.ADMISSION_TYPE],
        ['Admit source', e.ADMIT_SOURCE],
        ['Hospital service', e.HOSPITAL_SERVICE],
        ['Attending', e.ATTENDING_DOCTOR],
        ['Admitting', e.ADMITTING_DOCTOR],
        ['Referring', e.REFERRING_DOCTOR],
        ['Admit', e.ADMIT_DATETIME],
        ['Discharge', e.DISCHARGE_DATETIME],
        ['Disposition', e.VISIT_DISCHARGE_DISPOSITION],
        ['Diagnoses', e.DIAGNOSES],
        ['Insurance', e.INSURANCE],
        ['Message ID', e.MESSAGE_ID],
      ];
      return rows
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([label, value]) => ({ label, value }));
    },
  },
};
</script>
